import type { BillBook } from "./bills.ts";

/**
 * Where the till keeps its open bills between one launch and the next.
 *
 * Unfired lines exist nowhere else — the server holds only what has been sent
 * to the station — so a reload that lost this would lose a service in
 * progress, which is the one thing the offline story is supposed to make
 * impossible.
 *
 * **IndexedDB and not `localStorage`, and that is not a preference.** A bill
 * carries money and quantities as `bigint`, and `JSON.stringify` throws on
 * one. Structured clone stores them as they are.
 *
 * Its own database rather than a second store inside the queue's: a version
 * bump on the database holding an unsent settlement is the riskiest migration
 * in this product, and there is no reason to take it for this.
 */

const STORE = "bills";
const KEY = "book";

export type BillStorage = {
  read(): Promise<BillBook | null>;
  write(book: BillBook): Promise<void>;
};

export function indexedDbBillStorage(
  factory: IDBFactory,
  databaseName = "tovi-pos-bills",
): BillStorage {
  let opened: Promise<IDBDatabase> | null = null;

  function open(): Promise<IDBDatabase> {
    opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(databaseName, 1);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE)) database.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    return opened;
  }

  async function transact<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await open();
    return new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  return {
    read: () =>
      transact("readonly", (store) => store.get(KEY) as IDBRequest<BillBook | undefined>).then(
        (book) => book ?? null,
      ),
    write: async (book) => {
      await transact("readwrite", (store) => store.put(book, KEY) as IDBRequest<IDBValidKey>);
    },
  };
}

/** For tests, and for a browser with no IndexedDB to give us. */
export class MemoryBillStorage implements BillStorage {
  #book: BillBook | null = null;

  async read(): Promise<BillBook | null> {
    return this.#book;
  }

  async write(book: BillBook): Promise<void> {
    this.#book = book;
  }
}
